/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import { addFlashMessage, setError, setSuccess } from '../actions'
import React, { ReactNode } from 'react'
import clsx from 'clsx'
import { IS_DEV, ROOT_API_WEB } from '../config'
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { setAjaxLoading as setCommonAjaxLoading, setDebug as setCommonDebug } from 'store/common/CommonReducer'
import { reset as resetSession } from 'store/session/SessionReducer'
import i18n from 'helpers/i18n'

interface FlashMessage {
    message: string
    type: string
}

interface ApiResponse {
    success?: boolean
    message?: string
    redirect?: string
    data?: any
    flashMessages?: FlashMessage[]
}

interface ErrorResponse {
    status: number
    data: {
        errors?: Record<string, string[]>
        message?: string
        code?: number
        data?: {
            messages?: Record<string, string[]>
        }
    }
    config?: InternalAxiosRequestConfig
}

const AxiosCommon = {
    applyRequestInterceptor(instance: AxiosInstance, setError: any, store: any) {
        instance.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                if ((config as any).loader === undefined || (config as any).loader === true) {
                    store.dispatch(
                        setCommonAjaxLoading({
                            loading: 1,
                            url: config.url || '',
                        })
                    )
                }

                const session = store.getState().sessionState.session
                if (session && session.access_token) {
                    config.headers = (config.headers || {}) as any
                    config.headers['Authorization'] = `Bearer ${session.access_token}`
                }

                return config
            },
            (error: AxiosError) => {
                const shouldHandleLoader =
                    (error.config as any)?.loader === undefined || (error.config as any)?.loader === true

                if (shouldHandleLoader) {
                    store.dispatch(setCommonAjaxLoading({ loading: -1, url: '' }))
                }

                return Promise.reject(error)
            }
        )
    },

    applyResponseInterceptor(instance: AxiosInstance, showMessageFunction: (message: ReactNode) => any, store: any) {
        instance.interceptors.response.use(
            async (response: AxiosResponse<any>) => {
                let camel = response.data
                const shouldHandleLoader =
                    (response.config as any).loader === undefined || (response.config as any).loader === true

                const stopLoader = () => {
                    if (!shouldHandleLoader) {
                        return
                    }

                    store.dispatch(
                        setCommonAjaxLoading({
                            loading: -1,
                            url: response.request?.responseURL?.replace(ROOT_API_WEB, '') || '',
                        })
                    )
                }

                // Handle case where response is a JSON blob (common in some export redirections)
                if (camel instanceof Blob && camel.type === 'application/json') {
                    try {
                        const text = await camel.text()
                        camel = JSON.parse(text)
                    } catch (e) {
                        // Keep as blob if parsing fails
                    }
                }

                // If the response is a standard success with a redirect URL for a file download
                if (
                    camel &&
                    camel.success &&
                    (camel.data?.offer || camel.data?.file) &&
                    (response.config as any).responseType === 'blob'
                ) {
                    // Close the original request loading state before following the download URL.
                    stopLoader()

                    const downloadUrl = camel.data.offer || camel.data.file
                    const downloadName = camel.data.download_as

                    return instance
                        .get(downloadUrl, {
                            ...response.config,
                            method: 'get',
                            responseType: 'blob',
                            // @ts-ignore
                            loader: false, // Prevents double loading increment
                        })
                        .then((blobResponse: any) => {
                            if (downloadName && blobResponse) {
                                // Attach filename to the blob object so the caller can use it
                                blobResponse.name = downloadName
                            }
                            return blobResponse
                        })
                }

                // Standard loader handling for the current request
                stopLoader()

                // Handle direct blob returns
                if (camel instanceof Blob) {
                    return camel
                }

                if (camel && camel.redirect) {
                    window.location.href = camel.redirect
                    return Promise.reject(response)
                }

                // @ts-ignore
                if (typeof camel === 'string' && camel.startsWith('<')) {
                    store.dispatch(setCommonDebug(camel))
                    return Promise.reject(response)
                }

                if (typeof camel.success === 'undefined') {
                    store.dispatch(setError(i18n.t('Missing server reply. Probably a bug.')))
                    return Promise.reject(response)
                }

                if (camel.success === false) {
                    store.dispatch(setError(camel.message || i18n.t('General error')))
                    return Promise.reject(response)
                }

                if (camel.success === true) {
                    if (camel.message && camel.message !== 'OK') {
                        store.dispatch(setSuccess(<div>{camel.message}</div>))
                    }
                }

                if (camel.flashMessages) {
                    camel.flashMessages.forEach((flash: FlashMessage) =>
                        store.dispatch(addFlashMessage(<div>{flash.message}</div>, flash.type))
                    )
                }

                if (!camel.data) {
                    store.dispatch(setError(i18n.t('Missing server reply. Probably a bug.')))
                    return Promise.reject(response)
                }

                return camel.data
            },
            (error: AxiosError<any>) => {
                const shouldHandleLoader =
                    (error.config as any)?.loader === undefined || (error.config as any)?.loader === true

                if (shouldHandleLoader) {
                    store.dispatch(setCommonAjaxLoading({ loading: -1, url: '' }))
                }

                const isCypressTest =
                    typeof window !== 'undefined' && Boolean((window as any).Cypress)

                console.error('Axios Error Interceptor:', error)
                if (error.response) {
                    console.error('Axios Error Response:', error.response)
                }

                if (error.response) {
                    const { status, data, config } = error.response as ErrorResponse

                    if (status === 401) {
                        if (!isCypressTest) {
                            store.dispatch(resetSession())
                        }
                        return Promise.reject(error.response)
                    }
                    if (status === 403) {
                        store.dispatch(
                            showMessageFunction(<div className={clsx('errorMessage')}>{i18n.t('Access denied')}</div>)
                        )
                        return Promise.reject(error.response)
                    }

                    // @ts-ignore
                    if (typeof data === 'string' && data.startsWith('<')) {
                        store.dispatch(setCommonDebug(data))
                        return Promise.reject(error.response)
                    }

                    if (data.errors) {
                        store.dispatch(setCommonDebug(data))
                        store.dispatch(
                            showMessageFunction(
                                <div className={clsx('message')}>
                                    {IS_DEV && (
                                        <span>
                                            Error {status}:<br />
                                        </span>
                                    )}
                                    {Object.values(data.errors).map((errors: string[], idx: number) => (
                                        <div key={idx}>{errors.join(', ')}</div>
                                    ))}
                                </div>
                            )
                        )
                    } else if (data.message) {
                        const messagesInData = data?.data?.messages
                        const messageList: ReactNode[] = []

                        if (data.code === 115 && messagesInData) {
                            Object.keys(messagesInData).forEach((key) => {
                                messagesInData[key].forEach((item: string) => {
                                    messageList.push(<div key={item}>{item}</div>)
                                })
                            })
                        }

                        store.dispatch(
                            showMessageFunction(
                                <div className={clsx('errorMessage')}>
                                    {IS_DEV && (
                                        <div>
                                            Error {status}: {config?.url}
                                        </div>
                                    )}
                                    {messageList.length > 0 ? messageList : data.message || error.message}
                                </div>
                            )
                        )
                    } else {
                        store.dispatch(
                            showMessageFunction(
                                <div className={clsx('errorMessage')}>
                                    {IS_DEV && (
                                        <div>
                                            {i18n.t('Error')} {status}: {config?.url}
                                        </div>
                                    )}
                                    {i18n.t('An unexpected error occurred')}
                                </div>
                            )
                        )
                    }
                } else {
                    store.dispatch(showMessageFunction(<div>{i18n.t('Are you connected to the internet? CORS?')}</div>))
                }

                return Promise.reject(error.response || error)
            }
        )
    },
}

export default AxiosCommon
